import {KpisList} from '@domain/kpis'
import {HierarchyNodeType} from '@domain/shared'
import {
  getComparisonDate,
  getQuarterlyLabel,
  TabIndexDataMap,
  type AggregatedKpiData
} from '@features/performance-reports'
import {formatNumber} from '@hconnect/common/utils'
import {DateRange} from '@hconnect/uikit'
import {Column, Flag} from '@hconnect/uikit/src/lib2'
import ModeCommentIcon from '@mui/icons-material/ModeComment'
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined'
import {Box, IconButton, Typography} from '@mui/material'
import {DeltaLabel, kpiDeltaReversedColorMap, ActualKpiTemplates} from '@widgets/kpis'
import {capitalize} from 'lodash'
import moment from 'moment-timezone'

import {filterCommentsByLocation} from '../helpers/comments.helpers'
import {Comment, CommentLocationData, HierarchyNode, BarChartData} from '../types'

import {CountryItemHeader} from './CountryItemHeader'

const FRACTION_DIGITS = 0

const DELTA_MARGIN = 5

const getDeltaBarColor = (value: number) =>
  value > -DELTA_MARGIN && value < DELTA_MARGIN
    ? 'primary.light'
    : value > 0
      ? 'success.light'
      : 'error.light'

export const generateColumns = ({
  translationFn,
  currentTab,
  offsetYearsAmount,
  dateRange,
  locale,
  setCommentLocationData,
  comments,
  hierarchy,
  showQuarterlyData,
  isMobile
}: {
  translationFn: (key: string, inputParams?: {[key: string]: string}) => string
  currentTab: KpisList
  offsetYearsAmount: number
  dateRange: DateRange<Date>
  locale: string
  setCommentLocationData: (id: CommentLocationData) => void
  comments: Comment[]
  hierarchy?: HierarchyNode
  showQuarterlyData: boolean
  isMobile?: boolean
}): Column<AggregatedKpiData>[] => {
  const baseColumns: Column<AggregatedKpiData>[] = [
    {
      key: 'plant',
      label: translationFn('actualVsPlanned.label.plant'),
      customTemplate: ({locationData}) =>
        isMobile ? (
          locationData && <CountryItemHeader countryData={locationData} />
        ) : (
          <>
            {locationData?.countryCode && (
              <Flag
                countryCode={locationData.countryCode}
                alt={locationData.countryCode}
                height={14}
                sx={{
                  verticalAlign: 'inherit',
                  mr: 1
                }}
              />
            )}
            {locationData?.name}
          </>
        )
    }
  ]

  Array.from({length: offsetYearsAmount}, (_, i) => offsetYearsAmount - i).forEach((index) => {
    baseColumns.push({
      key: `planned${index}`,
      label: translationFn('actualVsPlanned.label.actualDate', {
        date: getComparisonDate(dateRange, locale, index)
      }),
      customTemplate: ({historicalData, unit}) => {
        const actual: number = historicalData.find(
          (historicalItem) => historicalItem.yearOffset && historicalItem.yearOffset === index
        )?.[TabIndexDataMap[currentTab].actual]
        return (
          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.text.disabled
            }}
          >
            {actual ? `${formatNumber(actual, locale)} ${unit}` : '-'}
          </Typography>
        )
      }
    })
  })

  baseColumns.push({
    key: 'plannedCurrentValue',
    label: (
      <Typography variant="caption" sx={{fontWeight: 'bold'}}>
        {translationFn('actualVsPlanned.label.plannedDate', {
          date: getComparisonDate(dateRange, locale)
        })}
      </Typography>
    ),
    customTemplate: (data) => {
      const planned = data[TabIndexDataMap[currentTab].planned] as number
      return planned === 0 ? '-' : `${formatNumber(planned, locale)} ${data.unit}`
    }
  })

  baseColumns.push({
    key: 'actualCurrentValue',
    label: (
      <Typography variant="caption" sx={{fontWeight: 'bold'}}>
        {translationFn('actualVsPlanned.label.actualDate', {
          date: getComparisonDate(dateRange, locale)
        })}
      </Typography>
    ),
    customTemplate: (data) => {
      const actual = data[TabIndexDataMap[currentTab].actual] as number
      const planned = data[TabIndexDataMap[currentTab].planned] as number
      const id = TabIndexDataMap[currentTab].id

      const result = actual === 0 ? '-' : `${formatNumber(actual, locale)} ${data.unit}`
      const Template = ActualKpiTemplates[id]
      return <Template actual={result} diff={actual - planned} />
    }
  })

  baseColumns.push({
    key: 'delta',
    label: 'Delta',
    customTemplate: (data) => {
      const id = TabIndexDataMap[currentTab].id
      const reverseColor = kpiDeltaReversedColorMap[id]
      return (
        <DeltaLabel value={data[TabIndexDataMap[currentTab].delta]} reverseColor={reverseColor} />
      )
    }
  })

  baseColumns.push({
    key: 'plannedYearly',
    label: translationFn('actualVsPlanned.label.plannedDate', {
      date: capitalize(moment().month(11).locale(locale).format("MMM 'YY"))
    }),
    customTemplate: (data) => {
      const planned = (data[TabIndexDataMap[currentTab].plannedYearly] || 0) as number
      return planned === 0 ? '-' : `${formatNumber(planned, locale)} ${data.unit}`
    }
  })

  if (showQuarterlyData) {
    const quarterlyLabel = getQuarterlyLabel(moment(dateRange.to).month())
    if (quarterlyLabel) {
      const year = moment(dateRange.to).locale(locale).format('YYYY')
      baseColumns.push({
        key: 'quarterlyForecast',
        label: `${quarterlyLabel} ${year}`,
        customTemplate: (data) => {
          const quarterlyForecastValue = data.quarterlyForecast.quarterlyForecastValue || 0
          return quarterlyForecastValue === 0
            ? '-'
            : `${formatNumber(quarterlyForecastValue, locale)} ${data.unit}`
        }
      })

      baseColumns.push({
        key: 'quarterlyForecastDelta',
        label: translationFn('actualVsPlanned.label.quarterlyDelta', {
          quarter: quarterlyLabel,
          year
        }),
        customTemplate: (data) => {
          const reverseColor = kpiDeltaReversedColorMap[TabIndexDataMap[currentTab].id]
          const quarterlyDelta = data.quarterlyForecast.delta || 0
          return <DeltaLabel value={quarterlyDelta} reverseColor={reverseColor} />
        }
      })
    }
  }

  baseColumns.push({
    key: 'comments',
    customTemplate: (data) => {
      const commentsCount =
        hierarchy &&
        filterCommentsByLocation(
          comments,
          hierarchy,
          data.locationData?.id || HierarchyNodeType.Global
        ).length
      const delta = data[TabIndexDataMap[currentTab].delta]
      if (!delta) return <></>
      return (
        <Box display="flex" alignItems="center">
          <IconButton
            color="primary"
            onClick={
              data.locationData
                ? (event) => {
                    event.stopPropagation()
                    if (data.locationData) {
                      setCommentLocationData({
                        delta,
                        locationData: data.locationData,
                        unit: data.unit,
                        actualCurrentValue: data[TabIndexDataMap[currentTab].actual],
                        plannedCurrentValue: data[TabIndexDataMap[currentTab].planned]
                      })
                    }
                  }
                : undefined
            }
            data-test-id={`comments-button-${data.locationData?.id}`}
          >
            {commentsCount ? <ModeCommentIcon /> : <ModeCommentOutlinedIcon />}
          </IconButton>

          {!!commentsCount && (
            <Typography
              variant="h5"
              sx={{color: 'primary.main'}}
              data-test-id={`comments-counter-${data.locationData?.id}`}
            >
              {commentsCount}
            </Typography>
          )}
        </Box>
      )
    }
  })

  return baseColumns
}

export const generateBarChartData = ({
  data,
  dateRange,
  currentTab,
  translationFn,
  locale
}: {
  data: AggregatedKpiData
  dateRange: DateRange<Date>
  currentTab: KpisList
  translationFn: (key: string, params?: {[key: string]: string}) => string
  locale: string
}): BarChartData => {
  const barData = data.historicalData.map((historicalItem) => ({
    name: translationFn('actualVsPlanned.label.actualDate', {
      date: getComparisonDate(dateRange, locale, historicalItem.yearOffset)
    }),
    label: (
      <Typography
        component="span"
        sx={{
          color: (theme) => theme.palette.text.disabled
        }}
      >{`${formatNumber(
        historicalItem[TabIndexDataMap[currentTab].actual] as number,
        locale,
        FRACTION_DIGITS
      )} ${data.unit}`}</Typography>
    ),
    value: historicalItem[TabIndexDataMap[currentTab].actual]
  }))
  const currentData = [
    {
      name: translationFn('actualVsPlanned.label.plannedDate', {
        date: getComparisonDate(dateRange, locale)
      }),
      label: `${formatNumber(
        data[TabIndexDataMap[currentTab].planned] as number,
        locale,
        FRACTION_DIGITS
      )} ${data.unit}`,
      value: data[TabIndexDataMap[currentTab].planned],
      backgroundColor: 'background.default',
      color: 'common.white'
    },
    {
      name: translationFn('actualVsPlanned.label.actualDate', {
        date: getComparisonDate(dateRange, locale)
      }),
      label: (
        <>
          <Box component="span" mr={2}>
            {formatNumber(
              data[TabIndexDataMap[currentTab].actual] as number,
              locale,
              FRACTION_DIGITS
            )}{' '}
            {data.unit}
          </Box>
          <DeltaLabel value={data[TabIndexDataMap[currentTab].delta]} />
        </>
      ),
      value: data[TabIndexDataMap[currentTab].actual],
      backgroundColor: getDeltaBarColor(data[TabIndexDataMap[currentTab].delta] as number),
      color: 'common.white'
    }
  ]
  return [...barData, ...currentData]
}
